import { Helmet } from 'react-helmet-async'
import { seoConfig } from '../config/siteConfig'

/**
 * SeoHead Component
 * 
 * @param {string} title - Page title (will be truncated to 60 chars)
 * @param {string} description - Meta description (will be truncated to 160 chars)
 * @param {string} canonical - Canonical URL path (e.g., '/products')
 * @param {string} type - Open Graph type (default: 'website')
 * @param {string} image - OG image path
 * @param {object} schema - JSON-LD schema object
 * @param {boolean} noindex - Force noindex (overrides seoEnabled)
 * @param {boolean} seoEnabled - Whether SEO is enabled for this page (default: true)
 */
const SeoHead = ({
  title,
  description,
  canonical,
  type = 'website',
  image = '/assets/images/logo.png',
  schema,
  noindex = false,
  seoEnabled = true
}) => {
  const { siteUrl, siteName, globalSeoEnabled } = seoConfig
  
  // Determine if we should noindex this page
  const shouldNoindex = noindex || !seoEnabled || !globalSeoEnabled
  
  // Ensure title is under 60 characters
  const formattedTitle = title.length > 60 ? title.substring(0, 57) + '...' : title
  
  // Ensure description is under 160 characters
  const formattedDescription = description.length > 160 
    ? description.substring(0, 157) + '...' 
    : description

  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : null
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{formattedTitle}</title>
      <meta name="title" content={formattedTitle} />
      <meta name="description" content={formattedDescription} />
      
      {/* Robots */}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL - only add if SEO is enabled */}
      {canonicalUrl && !shouldNoindex && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph / Facebook - only add if SEO is enabled */}
      {!shouldNoindex && <meta property="og:type" content={type} />}
      {!shouldNoindex && <meta property="og:url" content={canonicalUrl} />}
      {!shouldNoindex && <meta property="og:title" content={formattedTitle} />}
      {!shouldNoindex && <meta property="og:description" content={formattedDescription} />}
      {!shouldNoindex && <meta property="og:image" content={imageUrl} />}
      {!shouldNoindex && <meta property="og:site_name" content={siteName} />}
      
      {/* Twitter */}
      {!shouldNoindex && <meta name="twitter:card" content="summary_large_image" />}
      {!shouldNoindex && <meta name="twitter:url" content={canonicalUrl} />}
      {!shouldNoindex && <meta name="twitter:title" content={formattedTitle} />}
      {!shouldNoindex && <meta name="twitter:description" content={formattedDescription} />}
      {!shouldNoindex && <meta name="twitter:image" content={imageUrl} />}
      
      {/* Schema Markup - only add if SEO is enabled */}
      {schema && !shouldNoindex && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  )
}

// Schema generators
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SIBR Life Sciences Private Limited',
  alternateName: 'SIBR Lifescience',
  url: 'https://sibrlifescience.com',
  logo: 'https://sibrlifescience.com/assets/images/logo.png',
  description: 'SIBR Lifescience is a GMP-certified pharmaceutical company committed to developing high-quality healthcare products.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Shanti Niketan, Shop 102/B Raj Complex, Satyanarayan Park Main Rd',
    addressLocality: 'Rajkot',
    addressRegion: 'Gujarat',
    postalCode: '360007',
    addressCountry: 'IN'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@sibrlifescience.com',
    contactType: 'customer service'
  },
  sameAs: [
    'https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382'
  ]
})

export const generateProductSchema = (product) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.shortDescription,
  image: product.image ? `https://sibrlifescience.com${product.image}` : 'https://sibrlifescience.com/assets/images/logo.png',
  sku: product.id,
  brand: {
    '@type': 'Brand',
    name: 'SIBR Lifescience'
  },
  manufacturer: {
    '@type': 'Organization',
    name: 'SIBR Life Sciences Private Limited',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Baddi',
      addressRegion: 'Himachal Pradesh',
      addressCountry: 'IN'
    }
  },
  category: 'Pharmaceutical Products',
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: 'SIBR Life Sciences Private Limited'
    }
  }
})

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
})

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `https://sibrlifescience.com${item.url}` : undefined
  }))
})

export default SeoHead

